import React, { useEffect, useRef, useState } from "react";
import Loader from "../../Components/Loader/Loader";
import "./usermanagement.css";
import CommonTable, {
  TableRowAction,
} from "../../Components/CustomTable/CustomTable";
import SearchBar from "../../Components/SeachBar/searchbar";
import { MdOutlineFilterAlt } from "react-icons/md";
import { AddUserForm } from "../../Components/AddUser/AddUser";
import {
  GetRbacConfigService,
  GetAllClientsListService,
  CreateUserService,
  CreateUserPayload,
  DeleteUserService,
  DeactivateUserService,
} from "../../Services/UserManagementService/UserManagementService";
import { SelectOption } from "../../Components/AddUser/AddUser.types";
import AlertMessage from "../../Components/AlertMessage/AlertMessage";

// Maps an RbacManagedUser (from GetRbacConfigService) into the flat row shape
// both the table columns (name/email/role/clients/status) and EditUser's
// rowData (firstName/lastName/email/role/userId) expect. RBAC doesn't carry
// clients/status/lastActive for a user, so those are left blank rather than
// guessed — fill them in once/if the backend exposes them here.
const mapRbacUserToRow = (user: any, index: number) => ({
  id: user.id ?? index,
  userId: user.id,
  firstname: user.firstName ?? "",
  lastname: user.lastName ?? "",
  name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  email: user.email ?? "",
  role: user.role?.name ?? "",
  clients: "",
  status: user.status,
  lastActive: "",
});

export const UserManagement = () => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showfilter, setShowPopupFilter] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<number | undefined>(
    undefined,
  );
  const [open, setOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<TableRowAction | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [loadingOption, setLoadingOption] = useState(false);

  const fetchRbacConfig = async () => {
    try {
      const rbacConfig = await GetRbacConfigService();

      setRoleOptions(
        rbacConfig?.assignableRoles?.map((role: any) => ({
          label: role.name,
          value: role.name,
        })) ?? [],
      );

      // dataSource (the main table) is NOT set here — that's FetchData's job,
      // via getUsersSortedBy. Setting it from both here and FetchData raced
      // on every mount, causing the table to flicker between the two on reload.
    } catch (error) {
      console.error("Error fetching RBAC config:", error);
    }
  };

  // Seeds the "Assign Clients" list on the Add User form from the real
  // business clients list. Also used as the refresh handler for the button
  // next to the client search box (e.g. after creating a new client).
  const fetchClientsList = async () => {
    setLoadingOption(true);

    try {
      const clients = await GetAllClientsListService();

      setClientOptions(
        clients.map((client) => ({
          label: client.name,
          value: client.id,
        })),
      );
    } catch (error) {
      console.error("Failed to fetch clients list:", error);
    } finally {
      setLoadingOption(false);
    }
  };

  const handleCreateUser = async (values: CreateUserPayload) => {
    const result = await CreateUserService(values);

    if (result.success) {
      AlertMessage({ type: "success", text: result.message });
      setShowPopup(false);
      FetchData();
    } else {
      AlertMessage({ type: "error", text: result.message });
    }
  };

  // Toggles every row's checkbox at once; individual rows are kept in sync
  // via the selectAll effect below rather than being derived here.
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      const allIds = dataSource.map((row) => row.id);
      setSelectedRows(allIds);
      setSelectAll(true);
    }
  };

  const columns = [
    {
      key: "selected",
      title: (
        <input
          type="checkbox"
          checked={selectAll}
          onClick={(e) => e.stopPropagation()}
          onChange={handleSelectAll}
        />
      ),
      // No dataIndex: this column renders the checkbox control itself,
      // it isn't bound to a field on the row.
      dataIndex: "",
      isCheckbox: true,
      showSortArrows: false,
    },

    // { key: "id", title: "id", dataIndex: "id", showSortArrows: false },
    { key: "Name", title: "Name", dataIndex: "name", showSortArrows: true },
    { key: "Email", title: "Email", dataIndex: "email", showSortArrows: true },

    { key: "role", title: "Role", dataIndex: "role", showSortArrows: true },
    // {
    //   key: "clients",
    //   title: "Clients",
    //   dataIndex: "clients",
    //   showSortArrows: false,
    // },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      isStatus: true,
      useCircleStatus: true,
      showSortArrows: true,
    },
    // {
    //   // dataIndex is "status" here as well — left as-is, not a typo we
    //   // introduced. Flagging in case a distinct lastActive field is added later.
    //   key: "lastActive",
    //   title: "Last Active",
    //   dataIndex: "status",
    //   showSortArrows: false,
    // },
    {
      key: "actions",
      title: "",
      isActions: true,
    },
  ];
  // Initial load. Subsequent loads happen via the debounced search effect
  // below, or explicitly through sortBusinessClients/handleFilterChange.
  useEffect(() => {
    fetchRbacConfig();
    fetchClientsList();
    FetchData();
  }, []);

  const FetchData = async (
    reverse: boolean = false,
    key: string = "default",
    search: string = searchTerm,
    status?: number | null,
  ) => {
    try {
      setLoading(true);

      const resolvedStatus =
        status === null
          ? undefined
          : typeof status === "number"
            ? status
            : statusFilter;

      const result = await GetRbacConfigService({
        searchTerm: search || undefined,
        statuses: resolvedStatus === undefined ? undefined : [resolvedStatus],
        sortBy: key === "default" ? undefined : key,
        sortOrder: key === "default" ? undefined : reverse ? "desc" : "asc",
      });

      setDataSource((result?.users ?? []).map(mapRbacUserToRow));
    } catch (error) {
      setDataSource([]);
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Adds/removes a single row from the selection, keeping selectedRows as
  // the single source of truth for which rows are checked.
  const handleCheckboxChange = (row: any, checked: boolean) => {
    setSelectedRows((prev) => {
      if (checked) {
        if (!prev.includes(row.id)) return [...prev, row.id];
        return prev;
      } else {
        return prev.filter((id) => id !== row.id);
      }
    });
  };

  // Derives the header "select all" checkbox state from the current
  // selection instead of tracking it separately, so it stays correct
  // whether rows were (de)selected individually or via handleSelectAll.
  useEffect(() => {
    if (dataSource.length === 0) {
      setSelectAll(false);
      return;
    }

    setSelectAll(selectedRows.length === dataSource.length);
  }, [selectedRows, dataSource]);

  const handleClearSelection = () => {
    setSelectedRows([]);
    setSelectAll(false);
  };

  const selectedRowData = dataSource.filter((row) =>
    selectedRows.includes(row.id),
  );

  const isSingleSelection = selectedRows.length === 1;
  const hasActiveSelected = selectedRowData.some((row) => row.status === 1);
  const hasDeactivatedSelected = selectedRowData.some(
    (row) => row.status === 2,
  );
  const hasExpiredSelected = selectedRowData.some((row) => row.status === 3);

  const requestTableAction = (action: TableRowAction) => {
    setPendingAction(action);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedRows.length === 0) {
      setOpen(false);
    }
  }, [selectedRows.length]);

  const sortBusinessClients = async (reverse: boolean, key: string) => {
    try {
      setLoading(true);
      setSelectedRows([]);
      // The Key should be passed to the API to sort based on that key, For now we will use Name as thats the only one requested.
      await FetchData(reverse, key);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  // 1. New useEffect for "Live Search"
  useEffect(() => {
    // Create a timer to wait 500ms after the user stops typing
    const delayDebounceFn = setTimeout(() => {
      // Call your existing FetchData function
      FetchData(false, "default", searchTerm);
    }, 500);

    // Clean up the timer if the user types another letter before 500ms is up
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]); // This triggers every time searchTerm changes

  const handleFilterChange = (value: string) => {
    const statusByFilter: Record<string, number> = {
      pending: 0,
      active: 1,
      deactivated: 2,
      expired: 3,
    };

    if (value === "all") {
      setSelectedFilter("");
      setStatusFilter(undefined);
      FetchData(false, "default", searchTerm, null);
      setShowPopupFilter(false);
      return;
    }

    const isSameFilter = selectedFilter === value;
    const nextFilter = isSameFilter ? "" : value;
    const status = nextFilter ? statusByFilter[nextFilter] : undefined;

    setSelectedFilter(nextFilter);
    setStatusFilter(status);
    FetchData(false, "default", searchTerm, status ?? null);

    setShowPopupFilter(false);
  };

  useEffect(() => {
    if (!showfilter) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowPopupFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showfilter]);

  return (
    <div className="business-clients-container">
      <div className="business-clients-topbar">

        {/* Search */}
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm} // Every letter typed updates state
          onSearch={() => FetchData(false, "default", searchTerm)} // Button still works as a backup
        />

        {/* Filter */}
        <div className="filter-wrapper" ref={filterRef}>
          <MdOutlineFilterAlt
            size={35}
            className="filter-icon"
            onClick={() => setShowPopupFilter(true)}
          />

          {showfilter && (
            <div className="filter-menu">
              <label
                className="filter-option"
                style={{
                  background: selectedFilter === "" ? "#f3f4f6" : "transparent",
                  border:
                    selectedFilter === ""
                      ? "1px solid #e5e7eb"
                      : "1px solid transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFilter === ""}
                  onChange={() => handleFilterChange("all")}
                />
                <span>All</span>
              </label>

              <label
                className="filter-option"
                style={{
                  background:
                    selectedFilter === "pending" ? "#eef4ff" : "transparent",
                  border:
                    selectedFilter === "pending"
                      ? "1px solid #d7e6ff"
                      : "1px solid transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFilter === "pending"}
                  onChange={() => handleFilterChange("pending")}
                />
                <span>Pending</span>
              </label>

              <label
                className="filter-option"
                style={{
                  background:
                    selectedFilter === "active" ? "#eefaf1" : "transparent",
                  border:
                    selectedFilter === "active"
                      ? "1px solid #cfeed7"
                      : "1px solid transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFilter === "active"}
                  onChange={() => handleFilterChange("active")}
                />
                <span>Active</span>
              </label>

              <label
                className="filter-option"
                style={{
                  background:
                    selectedFilter === "deactivated"
                      ? "#fff4ea"
                      : "transparent",
                  border:
                    selectedFilter === "deactivated"
                      ? "1px solid #f9d7b4"
                      : "1px solid transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFilter === "deactivated"}
                  onChange={() => handleFilterChange("deactivated")}
                />
                <span>Deactivated</span>
              </label>

              <label
                className="filter-option"
                style={{
                  background:
                    selectedFilter === "expired" ? "#fef2f2" : "transparent",
                  border:
                    selectedFilter === "expired"
                      ? "1px solid #fecaca"
                      : "1px solid transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFilter === "expired"}
                  onChange={() => handleFilterChange("expired")}
                />
                <span>Expired</span>
              </label>

            </div>
          )}
        </div>

        {/* Existing Actions */}
        <div className="action-wrapper" ref={dropdownRef}>
          <button
            className="custom-btn"
            onClick={() => setOpen(!open)}
            disabled={selectedRows.length === 0}
          >
            Actions <span>{open ? "▲" : "▼"}</span>
          </button>

          {open && (
            <div className="dropdown-menu">
              <div className="dropdown-item">
                <button
                  disabled={!isSingleSelection}
                  title={
                    isSingleSelection
                      ? "Edit selected user"
                      : "Select a single user to edit"
                  }
                  onClick={() => requestTableAction("edit")}
                >
                  Edit
                </button>
              </div>

              <div className="dropdown-item">
                <button onClick={() => requestTableAction("delete")}>
                  Delete
                </button>
              </div>

              {hasActiveSelected && (
                <div className="dropdown-item">
                  <button onClick={() => requestTableAction("deactivate")}>
                    Deactivate User
                  </button>
                </div>
              )}

              {hasExpiredSelected && (
                <div className="dropdown-item">
                  <button onClick={() => requestTableAction("resendEmail")}>
                    Resend Invite
                  </button>
                </div>
              )}

              {hasDeactivatedSelected && (
                <div className="dropdown-item">
                  <button onClick={() => requestTableAction("reactivate")}>
                    Reactivate User
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <button onClick={() => setShowPopup(true)} className="custom-btn">
          Add User
        </button>

        <AddUserForm
          visible={showPopup}
          onClose={() => setShowPopup(false)}
          roleOptions={roleOptions}
          clientOptions={clientOptions}
          onSubmit={handleCreateUser}
          onClientsRefresh={fetchClientsList}
          loadingOption={loadingOption}
        />
      </div>

      {loading ? (
        <Loader />
      ) : dataSource.length === 0 ? (
        <h1 className="no-data-found">No data found</h1>
      ) : (
        <>
          <CommonTable
            sortInvoices={sortBusinessClients}
            columns={columns}
            data={dataSource}
            onRowCheckboxChange={handleCheckboxChange}
            selectedRowKeys={selectedRows}
            handleClearSelection={handleClearSelection}
            rowKey="id"
            fetchData={FetchData}
            isuser={true}
            deleteService={DeleteUserService}
            DeactivateUserService={DeactivateUserService}
            name="User"
            pendingAction={pendingAction}
            onPendingActionHandled={() => setPendingAction(null)}
          />
        </>
      )}
    </div>
  );
};